import FilterDialog from 'components/UI/filterDialog/FilterDialog.vue';
import { openComponentDialog } from 'src/design-system';

/**
 * Builds the one way this app opens the filter dialog.
 *
 * FilterChipsBar owns the dialog for a table that has filters to show, but a table with no client
 * or user filter yet replaces itself with a prompt to add one, and the bar is inside the table it
 * replaced. Those buttons need the same open: the same awaited preparation, the same guard, and
 * the same props, or the dialog they raise is subtly not the one the chips raise.
 *
 * Each call site gets its own opener, so the guard below is per button rather than shared between
 * every dialog on the page.
 *
 * @param {() => object} getOptions returns `onApply`, an optional `beforeOpen`, and the dialog's
 *   props, read at click time so a criteria list that is still loading is not captured early
 * @returns {(openAt?: string | null) => Promise<void>} opens the dialog, at `openAt` when given
 */
export default function createFilterDialogOpener(getOptions) {
  /*
   * Nothing on screen changes while beforeOpen is awaited, so a second click during a slow load
   * would open a second dialog over the same map, each with its own draft, and the later Apply
   * would drop the earlier one's edits.
   */
  let isOpening = false;

  const readOptions = () => {
    const { onApply, beforeOpen = null, ...dialogProps } = getOptions();
    return { onApply, beforeOpen, dialogProps };
  };

  return async function openFilterDialog(openAt = null) {
    if (isOpening) {
      return;
    }

    const { beforeOpen } = readOptions();

    isOpening = true;
    try {
      if (beforeOpen !== null) {
        await beforeOpen();
      }
    } catch (error) {
      /*
       * The criteria the dialog would read are the caller's to load and its failure is the
       * caller's to describe, so the open is abandoned rather than shown half-populated. Caught
       * all the same: an event handler's rejection surfaces nowhere, and leaving the flag set
       * would lock the button.
       */
      console.error('Error preparing the filter dialog:', error);
      return;
    } finally {
      isOpening = false;
    }

    /*
     * Read again rather than reusing what beforeOpen was taken from: the hook exists to load what
     * the dialog reads, so options gathered before it ran are the ones it was called to replace.
     */
    const { onApply, dialogProps } = readOptions();

    openComponentDialog(FilterDialog, { ...dialogProps, openAt }, onApply);
  };
}
